import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import * as Yup from "yup";
import { BRAND_BLUE } from "../../shared/pricingHelpers";

export interface LineItem {
  feature_key: string;
  qty: number | "";
}

export interface CalculatorPresetValues {
  key: string;
  name: string;
  meta: string;
  display_order: number | "";
  is_active: boolean;
  line_items: LineItem[];
}

const EMPTY: CalculatorPresetValues = {
  key: "",
  name: "",
  meta: "",
  display_order: 0,
  is_active: true,
  line_items: [{ feature_key: "", qty: 1 }],
};

interface Props {
  open: boolean;
  mode: "create" | "edit";
  initial?: Partial<CalculatorPresetValues> | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
}

const schema = Yup.object().shape({
  key: Yup.string().trim().required("Key is required"),
  name: Yup.string().trim().required("Name is required"),
  line_items: Yup.array()
    .of(
      Yup.object().shape({
        feature_key: Yup.string().trim().required("Feature key required"),
        qty: Yup.number().typeError("Qty required").min(1, "Qty must be ≥ 1"),
      })
    )
    .min(1, "Add at least one line item"),
});

const buildPayload = (v: CalculatorPresetValues) => ({
  key: v.key.trim(),
  name: v.name.trim(),
  meta: v.meta.trim() === "" ? null : v.meta.trim(),
  display_order: v.display_order === "" ? 0 : Number(v.display_order),
  is_active: v.is_active,
  line_items: v.line_items.map((li) => ({
    feature_key: li.feature_key.trim(),
    qty: Number(li.qty),
  })),
});

const CalculatorPresetsForm: React.FC<Props> = ({
  open,
  mode,
  initial,
  saving,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<CalculatorPresetValues>(EMPTY);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (open) {
      const next = { ...EMPTY, ...(initial || {}) } as CalculatorPresetValues;
      if (!next.line_items || next.line_items.length === 0) {
        next.line_items = [{ feature_key: "", qty: 1 }];
      }
      setValues(next);
      setError("");
    }
  }, [open, initial]);

  const set = (name: keyof CalculatorPresetValues, value: any) =>
    setValues((p) => ({ ...p, [name]: value }));

  const setLine = (idx: number, field: keyof LineItem, value: any) =>
    setValues((p) => ({
      ...p,
      line_items: p.line_items.map((li, i) =>
        i === idx ? { ...li, [field]: value } : li
      ),
    }));

  const addLine = () =>
    setValues((p) => ({
      ...p,
      line_items: [...p.line_items, { feature_key: "", qty: 1 }],
    }));

  const removeLine = (idx: number) =>
    setValues((p) => ({
      ...p,
      line_items: p.line_items.filter((_, i) => i !== idx),
    }));

  const handleSubmit = async () => {
    try {
      await schema.validate(values, { abortEarly: false });
      setError("");
      onSubmit(buildPayload(values));
    } catch (err: any) {
      setError(err.errors?.[0] || "Please fix the highlighted fields.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {mode === "create" ? "Add Calculator Preset" : "Edit Calculator Preset"}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Key"
              value={values.key}
              onChange={(e) => set("key", e.target.value)}
              helperText="Stable id, e.g. staffing"
              size="small"
            />
            <TextField
              label="Name"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              size="small"
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 2 }}>
            <TextField
              label="Meta"
              value={values.meta}
              onChange={(e) => set("meta", e.target.value)}
              helperText="Optional, e.g. ~50 hires"
              size="small"
            />
            <TextField
              label="Display Order"
              type="number"
              value={values.display_order}
              onChange={(e) =>
                set("display_order", e.target.value === "" ? "" : Number(e.target.value))
              }
              size="small"
            />
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475467" }}>
                Line items
              </Typography>
              <Button
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={addLine}
                sx={{ textTransform: "none" }}
              >
                Add line
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {values.line_items.map((li, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 1.5 }}
                >
                  <TextField
                    label="Feature key"
                    value={li.feature_key}
                    onChange={(e) => setLine(idx, "feature_key", e.target.value)}
                    size="small"
                  />
                  <TextField
                    label="Qty"
                    type="number"
                    value={li.qty}
                    onChange={(e) =>
                      setLine(idx, "qty", e.target.value === "" ? "" : Number(e.target.value))
                    }
                    size="small"
                  />
                  <Tooltip title="Remove line">
                    <span>
                      <IconButton
                        onClick={() => removeLine(idx)}
                        disabled={values.line_items.length === 1}
                        sx={{ color: "#d92d20" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={values.is_active}
                onChange={(e) => set("is_active", e.target.checked)}
                color="primary"
              />
            }
            label="Active"
          />

          {error && (
            <Typography sx={{ color: "error.main", fontSize: 13 }}>{error}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: BRAND_BLUE,
            "&:hover": { backgroundColor: "#0445a3" },
          }}
        >
          {mode === "create" ? "Create" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalculatorPresetsForm;
